import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useTheme } from '@evi/skins';
import { HOST_PROGRAM_PATH } from '@evi/shared';
import { verifyMagicLink } from '@/lib/api';
import { consumePendingDeepLink } from '@/lib/pendingDeepLink';
import { reregisterPush } from '@/lib/push';
import { clearStashedFleetInviteToken } from '@/pages/fleet/JoinFleetPage';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import i18n from '@/lib/i18n';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/Form.module.css';
import { FormError, useFormError } from '@/components/ui/FormError';

interface Props { onNavigate: (path: string) => void }

type VerifyResult = Awaited<ReturnType<typeof verifyMagicLink>>;

export function MagicLinkVerifyPage({ onNavigate }: Props) {
  const { t } = useTranslation('auth');
  const setAuth = useAuthStore((s) => s.setAuth);
  // Theme-persistence rework — see LoginPage.tsx for full rationale.
  // Respect the driver's server-saved `active_theme_family` +
  // `theme_mode` instead of force-resetting to Initiative-dark, so
  // magic-link login on a returning device preserves the theme the
  // driver had applied.
  const { setThemeId, setMode } = useTheme();
  const [status, setStatus] = useState<'verifying' | 'linked' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useFormError();
  // Held between the exchange and the driver's tap on Continue when the
  // Google callback flagged a first link (?notice=google_linked, see
  // google.ts: the account's previous password was just rotated). The
  // session is stored only on Continue, so the notice is read before the
  // app takes over.
  const [pending, setPending] = useState<VerifyResult | null>(null);
  const fromHostProgram = new URLSearchParams(window.location.search).get('flow') === 'host_program';

  const complete = useCallback((res: VerifyResult) => {
    setAuth(res.user, res.access_token);
    // Respect server-saved theme preference (see component
    // header). Falls through silently when fields are absent
    // (new accounts, pre-migration records).
    if (res.user.active_theme_family) {
      setThemeId(res.user.active_theme_family);
    }
    if (res.user.theme_mode) {
      setMode(res.user.theme_mode);
    }
    // Refresh ALWAYS: this page is reachable while signed in (an emailed
    // link finishes a pending fleet join), and then there is no auth flip,
    // so App's own refresh never runs - and the verify response's user has
    // no fleet fields, so without this the store would hold a stripped user.
    void useAuthStore.getState().refreshUser();
    // A parked invite (carried through a Google sign-in) is spent either way.
    clearStashedFleetInviteToken();
    // A pending fleet join (email invite or the fleet's email domain)
    // applies only at a verified sign-in, i.e. this one. The join signed
    // out other devices by dropping every push registration on the account,
    // this device's too: put this device back (no prompt; only if allowed).
    // i18n.t, not the hook's t: `complete` must stay stable, since the
    // verify effect re-runs (and re-spends the one-use token) when it changes.
    if (res.fleet_joined) {
      void reregisterPush();
      toast(i18n.t('auth:toast.fleetJoined', { fleetName: res.fleet_joined.fleet_name }), { tone: 'accent', durationMs: 4000 });
    } else if (res.fleet_join_deferred) {
      // A pending join is still waiting - say so rather than stay silent.
      toast(i18n.t('auth:toast.fleetJoinDeferred'), { tone: 'warn', durationMs: 8000 });
    }
    // Replay a stashed charger deep link (shared link / QR opened
    // while signed out) — else the map, the founder's default
    // lander. The Google flow exits through this page too, so
    // both no-password sign-ins inherit the same landing, and the
    // sessionStorage stash survives the OAuth redirect round-trip.
    const pendingPath = consumePendingDeepLink();
    onNavigate(fromHostProgram ? HOST_PROGRAM_PATH : pendingPath ?? '/map');
  }, [setAuth, setThemeId, setMode, onNavigate, fromHostProgram]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email');
    const token = params.get('token');
    const notice = params.get('notice');

    if (!email || !token) {
      setStatus('error');
      setErrorMsg('Missing email or token in the link');
      return;
    }

    verifyMagicLink(email, token)
      .then((res) => {
        if (notice === 'google_linked') {
          setPending(res);
          setStatus('linked');
          return;
        }
        complete(res);
      })
      .catch((err) => {
        setStatus('error');
        setErrorMsg(err instanceof Error ? err.message : 'Invalid or expired link');
      });
  }, [complete]);

  const title = status === 'verifying'
    ? 'Signing you in...'
    : status === 'linked'
      ? t('googleLinked.title')
      : 'Sign-in failed';
  const subtitle = status === 'verifying'
    ? 'Verifying your magic link'
    : status === 'linked'
      ? t('googleLinked.subtitle')
      : 'The link may have expired';

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      {status === 'verifying' ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '32px 0' }}>
          <Loader2 size={36} className="spin" style={{ color: 'var(--color-brand)' }} />
        </div>
      ) : status === 'linked' ? (
        <>
          <div role="status" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <ShieldCheck size={20} style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-brand)' }} />
            <p style={{ margin: 0 }}>{t('googleLinked.body')}</p>
          </div>
          <button
            className={formStyles.btnPrimary}
            onClick={() => { if (pending) complete(pending); }}
          >
            {t('googleLinked.continue')}
          </button>
        </>
      ) : (
        <>
          <FormError style={{ display: 'flex', gap: 10 }} announce={errorMsg?.nonce}>
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{errorMsg?.message}</span>
          </FormError>
          <button className={formStyles.btnPrimary} onClick={() => onNavigate(fromHostProgram ? HOST_PROGRAM_PATH + '?auth=retry' : '/auth/magic')}>
            Request a new link
          </button>
          <button className={formStyles.btnSecondary} onClick={() => onNavigate(fromHostProgram ? HOST_PROGRAM_PATH + '?auth=login' : '/login')}>
            Use password instead
          </button>
        </>
      )}
      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </AuthLayout>
  );
}
